import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { DropdownModule } from 'primeng/dropdown';
import { MessageService, SharedModule } from 'primeng/api';
import { FormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';
import { Subscription, switchMap, take } from 'rxjs';
import { ApplicationProfileService } from '@shared/services/application-profile.service';
import { Merchant, RepresentativeMerchantAccess } from '@shared/models';
import { MerchantService } from '@app/resources/services/merchant.service';
import { RepresentativesApiService } from '@app/areas/admin/representatives/services/representatives.api.service';

type RepresentativeMerchantsCardMode = 'view' | 'edit';

@Component({
  selector: 'app-representative-merchants-card',
  templateUrl: './representative-merchants-card.component.html',
  styleUrls: ['./representative-merchants-card.component.scss'],
  standalone: true,
  imports: [ButtonModule, TableModule, DropdownModule, SharedModule, FormsModule, NgIf],
})
export class RepresentativeMerchantsCardComponent implements OnChanges, OnDestroy {
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly merchantService = inject(MerchantService);
  private readonly representativesApiService = inject(RepresentativesApiService);
  private readonly messageService = inject(MessageService);

  @Input({ required: true }) representativeId!: number;
  @Output() merchantClicked = new EventEmitter<number>();

  protected mode: RepresentativeMerchantsCardMode = 'view';
  protected isLoading = false;
  protected isSaving = false;
  protected merchants: RepresentativeMerchantAccess[] = [];
  protected availableMerchants: Merchant[] = [];
  protected merchantToAdd: number | null = null;

  private committedMerchants: RepresentativeMerchantAccess[] = [];
  private subs = new Subscription();

  protected get addableMerchants(): Merchant[] {
    const selectedMerchantIds = new Set(this.merchants.map((merchant) => merchant.MerchantId));
    return this.availableMerchants.filter((merchant) => !selectedMerchantIds.has(merchant.MerchantId));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['representativeId']) {
      this.mode = 'view';
      this.loadMerchantAccess();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected edit(): void {
    this.merchants = [...this.committedMerchants];
    this.merchantToAdd = null;
    this.mode = 'edit';

    this.subs.add(
      this.applicationProfileService.applicationProfile$
        .pipe(
          take(1),
          switchMap((profile) => this.merchantService.findAll(profile.Representative.CoalitionId))
        )
        .subscribe((result: any) => {
          this.availableMerchants = (result?.$values ?? [])
            .map(mapToMerchant)
            .sort((a: Merchant, b: Merchant) => a.Name.localeCompare(b.Name));
        })
    );
  }

  protected removeMerchant(merchantId: number): void {
    this.merchants = this.merchants.filter((merchant) => merchant.MerchantId !== merchantId);
  }

  protected addMerchant(merchantId: number | null): void {
    const merchant = this.availableMerchants.find((availableMerchant) => availableMerchant.MerchantId === merchantId);
    if (merchant) {
      this.merchants = [
        ...this.merchants,
        { MerchantAccessId: 0, MerchantId: merchant.MerchantId, MerchantName: merchant.Name },
      ];
    }

    this.merchantToAdd = null;
  }

  protected cancel(): void {
    this.merchants = this.committedMerchants;
    this.mode = 'view';
  }

  protected save(): void {
    this.isSaving = true;
    const merchantIds = this.merchants.map((merchant) => merchant.MerchantId);

    this.subs.add(
      this.representativesApiService.saveMerchantAccess(this.representativeId, merchantIds).subscribe((success) => {
        this.isSaving = false;
        if (success) {
          this.mode = 'view';
          this.loadMerchantAccess();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'ERROR: Merchant Access',
            detail: 'Something went wrong while saving merchant access',
          });
        }
      })
    );
  }

  private loadMerchantAccess(): void {
    this.isLoading = true;
    this.subs.add(
      this.representativesApiService.getMerchantAccess(this.representativeId).subscribe((merchantAccesses) => {
        this.isLoading = false;
        const sortedMerchantAccesses = [...merchantAccesses].sort((a, b) =>
          a.MerchantName.localeCompare(b.MerchantName)
        );
        this.committedMerchants = sortedMerchantAccesses;
        this.merchants = sortedMerchantAccesses;
      })
    );
  }
}

function mapToMerchant(data: any): Merchant {
  return {
    MerchantId: data.merchantId,
    Name: data.name,
  };
}
